import { ButtonEmphasis, ButtonRole } from '../types'
import { css, FlattenSimpleInterpolation } from 'styled-components'
import { colors } from '../../../../defaultTheme'
import { extendedColord as colord } from '../../../../lib/extendedColord'
import { Loader } from '../../../../Loader'
import SvgIcon from '../../Icon/SvgIcon'

// I slightly bent the result, in order to be able to abstract away as most of the
// variability between button parameters. The resulting buttons do not EXACTLY match
// the Figma designs, but I think this is close enough, and more than acceptable.
export const getButtonColors = (
  role: ButtonRole,
  emphasis: ButtonEmphasis,
  disabled: boolean,
  disableFocusOnClick: boolean
) => {
  const schemaForRole = colorsByRole[role]

  return [
    getContentColors(schemaForRole, emphasis), // I <3 PRETTIER
    getBoxColors(schemaForRole, emphasis, disabled, disableFocusOnClick),
    disabled && getDisabledOverrides(emphasis)
  ]
}

type ButtonColorSchema = {
  backgroundNeutral: string
  backgroundActive: string
  text: string
}

const colorsByRole: Record<ButtonRole, ButtonColorSchema> = {
  neutral: {
    backgroundNeutral: colors.blue,
    backgroundActive: colors.darkBlue,
    text: colors.white
  },
  negative: {
    backgroundNeutral: colors.red,
    backgroundActive: colors.darkRed,
    text: colors.white
  },
  positive: {
    backgroundNeutral: colors.green,
    backgroundActive: colors.darkGreen,
    text: colors.white
  }
}

const invertSchema = (schemaIn: ButtonColorSchema): ButtonColorSchema => ({
  backgroundNeutral: colors.white,
  backgroundActive: colors.silverLight,
  text: schemaIn.backgroundActive
})

const getDisabledOverrides = (emphasis: ButtonEmphasis) => css`
  background: ${colors.silverMedium};
  color: ${colors.white};
  border: 1px solid ${colors.silverMedium};

  ${emphasis === 'flat' &&
  css`
    background: transparent;
    color: ${colors.silverMedium};
    border: none;

    & ${Loader} .primary,
    ${SvgIcon}, ${SvgIcon} .primary {
      color: ${colors.silverMedium};
      stroke: ${colors.silverMedium};
    }
  `};
`

const getContentColors = (schemaForRole: ButtonColorSchema, emphasis: ButtonEmphasis) => {
  const emphasisAdjustedSchema =
    emphasis === 'primary' // I <3 PRETTIER
      ? schemaForRole
      : invertSchema(schemaForRole)

  return css`
    background: ${emphasisAdjustedSchema.backgroundNeutral};
    background: ${emphasis === 'flat' && 'transparent;'};

    color: ${emphasisAdjustedSchema.text};

    & ${Loader} .primary,
    ${SvgIcon}, ${SvgIcon} .primary {
      color: ${emphasisAdjustedSchema.text};
      stroke: ${emphasisAdjustedSchema.text};
    }
  `
}

const getBoxColors = (
  schemaForRole: ButtonColorSchema,
  emphasis: ButtonEmphasis,
  disabled: boolean,
  disableFocusOnClick: boolean
) => {
  switch (emphasis) {
    case 'primary':
      return !disabled && getPrimaryButtonBoxColors(schemaForRole, disableFocusOnClick)

    case 'secondary':
      return !disabled && getSecondaryButtonBoxColors(schemaForRole, disableFocusOnClick)

    case 'flat':
      return getFlatButtonBoxColors(schemaForRole, disableFocusOnClick)
  }

  throw new Error(`You tried to render button with a non-defined emphasis: ${emphasis}`)
}

const getPrimaryButtonBoxColors = (schema: ButtonColorSchema, disableFocusOnClick: boolean) => {
  const focusCss = css`
    box-shadow: 0px 0px 0px 4px ${schema.backgroundNeutral}32, 0px 1px 3px rgba(0, 0, 0, 0.222);
  `

  return css`
    border: none;

    &:hover,
    &:active {
      background: ${schema.backgroundActive};
    }

    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.222);

    ${onFocus(disableFocusOnClick, focusCss)}
  `
}

const getSecondaryButtonBoxColors = (schema: ButtonColorSchema, disableFocusOnClick: boolean) => {
  const focusCss = css`
    border-color: ${schema.backgroundActive};
    background: ${colors.silverLight};

    box-shadow: 0px 0px 0px 4px ${colord(schema.backgroundActive).lighten(0.15).toHex()}32,
      0px 1px 2px rgba(0, 0, 0, 0.0452719);
  `

  return css`
    border: 1px solid ${colors.silver};

    &:hover,
    &:active {
      border-color: ${schema.backgroundActive};
      background: ${colors.silverLight};
    }

    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.0452719);

    ${onFocus(disableFocusOnClick, focusCss)}
  `
}

const getFlatButtonBoxColors = (
  invertedSchema: ButtonColorSchema,
  disableFocusOnClick: boolean
) => [
  getSecondaryButtonBoxColors(invertedSchema, disableFocusOnClick),
  css`
    &:not(:focus) {
      border-color: ${colors.silverMedium};
    }

    &:not(:hover):not(:focus):not(:active) {
      border: none;
      box-shadow: none;
    }
  `
]

// This is quite clunky, but it was the most reasonable way I could get working with the css`` macro syntax
const onFocus = (preventFocusOnClick: boolean, cssRules: FlattenSimpleInterpolation | string) => {
  if (preventFocusOnClick) {
    return css`
      &:focus-visible {
        ${cssRules};
      }
    `
  }

  return css`
    &:focus {
      ${cssRules};
    }
  `
}
